"use client";

import {MouseEventHandler, useCallback} from "react";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import prefersReducedMotion from "../util/prefersReducedMotion";
import LinkButton, {LinkButtonProps} from "./LinkButton";

interface ScrollingLinkButtonProps extends LinkButtonProps {
	targetId: string;
	scrollOptions?: ScrollIntoViewOptions;
}

/**
 * A button that scrolls to a target element on the page when clicked.
 *
 * @param targetId The id of the element to scroll to.
 * @param scrollOptions Options for the scroll behavior.
 */
const ScrollingLinkButton: FCWithHTMLAttributes<ScrollingLinkButtonProps> = ({
	targetId,
	scrollOptions = {behavior: "smooth", block: "start"},
	...props
}) => {
	const onClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
		(e) => {
			// Don't smooth scroll ever if the user prefers reduced motion
			if (prefersReducedMotion()) {
				scrollOptions.behavior = "instant";
			}
			const target = document.getElementById(targetId);
			if (target) {
				e.preventDefault();
				target.scrollIntoView(scrollOptions);
			}
			// if target isn't found, will behave like a normal link
		},
		[targetId, scrollOptions]
	);
	return <LinkButton onClick={onClick} {...props} />;
};

export default ScrollingLinkButton;
